import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  sessionRecordings, startRecording, answerConsent, finishRecording, notReady, reasonOf, RECORDING_LANGUAGES, MIXED_HINT, defaultLanguageWords
} from '../../lib/recordings';
import { ping, startRinging, stopRinging, buzz, flashTitle } from '../../lib/chime';
import { useMe } from '../../lib/me';
import { useFeature } from '../../lib/features';
import { Dialog } from '../todos/TodoPeople';
import { useRecordingState, getRecordingState, setRecordingState, uploader } from './store';
import './Recorder.css';

export { default as RecordingBanner } from './RecordingBanner';

/**
 * RECORDING A CALL, at the user's option (Gordon, 22 October).
 *
 * The person recording presses Record in the footer, chooses sound only or
 * sound and picture, and from then on EVERYBODY on the call sees the banner
 * (RecordingBanner.js) and hears a chime - consent by announcement, which is
 * the firm's default. With the firm's "Before recording" rule set to ask,
 * nothing is captured until every other person on the call has pressed
 * Accept; one Decline cancels it and says who.
 *
 * WHAT IS CAPTURED, AND FROM WHERE. Not the server: the media never passes
 * through it - every pair of browsers talks directly (SessionPage's mesh) -
 * so the only place all the voices exist together is the recorder's own
 * page. The microphone and every remote audio track are mixed through an
 * AudioContext into one MediaStreamAudioDestinationNode; for video, a canvas
 * at 720p and 10 frames a second is painted with whatever the tiles are
 * showing - remote screens and cameras, my own camera - with names on them,
 * and captureStream() turns that into a track. Modest on purpose: this is a
 * support call's record, not a broadcast, and a laptop on a call is already
 * encoding one stream per other person.
 *
 * MediaRecorder hands over a chunk every five seconds and each goes straight
 * to the server, numbered, in order (store.js's uploader, which retries and
 * survives this component unmounting). Stop - or the call ending - sends the
 * last chunk and then `finish`, and the server takes it from there:
 * transcript, then whatever is asked of the transcript.
 *
 * ONE SIGNAL KIND, 'recording', carries everything between the pages:
 * started, stopped, asked, accepted, declined. SessionPage routes it here
 * through onRecordingSignal, which is the only thing it needs to know.
 *
 * SAFARI. MediaRecorder arrived in 14.1 and records audio/mp4 rather than
 * WebM; the mime is chosen from what isTypeSupported says yes to, and a
 * browser that says no to everything is told so in words rather than given a
 * button that fails.
 */

const AUDIO_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
const VIDEO_TYPES = ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
const TIMESLICE_MS = 5000;
const CANVAS = { w: 1280, h: 720, fps: 10 };

/** The first type this browser will record, or null when it records nothing. */
export const pickMime = (kind) => {
  const MR = typeof window !== 'undefined' ? window.MediaRecorder : null;
  if (!MR || typeof MR.isTypeSupported !== 'function') return null;
  const list = kind === 'video' ? VIDEO_TYPES : AUDIO_TYPES;
  return list.find((t) => { try { return MR.isTypeSupported(t); } catch { return false; } }) || null;
};

const NOT_SUPPORTED = 'Recording is not supported in this browser. Chrome, Edge, Firefox and Safari 14.1 or later can record.';

/* ------------------------------------------------------------- capture
 *
 * Module-level rather than in a ref: Stop and End both have to reach it after
 * the component that started it has gone, and there is only ever one.
 */
const capture = {
  recorder: null, ctx: null, dest: null, sources: new Map(), scan: null,
  canvas: null, paint: null, startedAt: 0, seq: 0, job: null, running: false, stream: null
};

/** Every audio stream on the call that is not yet in the mix: put it in. */
function mixIn(micStream, meshRef) {
  const { ctx, dest, sources } = capture;
  if (!ctx || !dest) return;
  const streams = [];
  if (micStream) streams.push(['mic', micStream]);
  for (const [id, entry] of (meshRef?.current || new Map())) {
    if (entry?.audioStream) streams.push([id, entry.audioStream]);
  }
  for (const [id, s] of streams) {
    if (sources.has(id) || !s.getAudioTracks || s.getAudioTracks().length === 0) continue;
    try {
      const node = ctx.createMediaStreamSource(s);
      node.connect(dest);
      sources.set(id, node);
    } catch { /* a stream with no live audio yet; the next scan tries again */ }
  }
}

/**
 * The tiles, as the canvas paints them: who is showing what, right now.
 *
 * AND WHAT IS DRAWN ON THEM (components/draw, 22 October). The whiteboard is
 * a frame of its own, cropped to its paper; a shared screen carries its
 * annotation layer, which Annotate sizes to the picture exactly, so painting
 * it over the same rectangle as the video lines the marks up in the file as
 * they were on the screen. My own screen, while I share it, is in the
 * preview tile and is recorded like anybody else's.
 */
function framesOnPage(nameOf, myName) {
  const out = [];
  if (typeof document === 'undefined') return out;
  const board = document.querySelector('canvas.dr-board-canvas');
  if (board && board.width > 0 && board.dataset.box) {
    const [x, y, w, h] = board.dataset.box.split(',').map(Number);
    if (w > 0 && h > 0) out.push({ el: board, name: 'Whiteboard', crop: { x, y, w, h } });
  }
  for (const v of document.querySelectorAll('.se-tile video.se-video[data-live], .se-preview video.se-video[data-live]')) {
    if (!(v.videoWidth > 0)) continue;
    const tile = v.closest('.se-tile, .se-preview');
    const id = tile?.getAttribute('data-id');
    const overlay = tile?.querySelector('canvas.dr-overlay');
    const name = tile?.classList.contains('se-preview') ? `${myName || 'You'} (screen)` : nameOf(id) || 'Somebody';
    out.push({ el: v, name, overlay: overlay && overlay.width > 1 ? overlay : null });
  }
  const self = document.querySelector('.se-self-video[data-live]');
  if (self && self.videoWidth > 0) out.push({ el: self, name: myName || 'You', mirror: true });
  return out;
}

/** A simple grid: 1, 2 side by side, 3-4 in a 2x2, more in a 3-wide. */
function paintFrame(c2d, frames, w, h) {
  c2d.fillStyle = '#101014';
  c2d.fillRect(0, 0, w, h);
  if (frames.length === 0) {
    c2d.fillStyle = '#9a9aa2';
    c2d.font = '600 36px system-ui, sans-serif';
    c2d.textAlign = 'center';
    c2d.fillText('Nothing is being shown', w / 2, h / 2);
    c2d.textAlign = 'left';
    return;
  }
  const cols = frames.length === 1 ? 1 : frames.length <= 4 ? 2 : 3;
  const rows = Math.ceil(frames.length / cols);
  const cw = w / cols;
  const ch = h / rows;
  frames.forEach((f, i) => {
    const x = (i % cols) * cw;
    const y = Math.floor(i / cols) * ch;
    const vw = f.crop ? f.crop.w : f.el.videoWidth || 16;
    const vh = f.crop ? f.crop.h : f.el.videoHeight || 9;
    const scale = Math.min((cw - 8) / vw, (ch - 8) / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    const dx = x + (cw - dw) / 2;
    const dy = y + (ch - dh) / 2;
    try {
      if (f.mirror) {
        c2d.save();
        c2d.translate(dx + dw, dy);
        c2d.scale(-1, 1);
        c2d.drawImage(f.el, 0, 0, dw, dh);
        c2d.restore();
      } else if (f.crop) {
        c2d.drawImage(f.el, f.crop.x, f.crop.y, f.crop.w, f.crop.h, dx, dy, dw, dh);
      } else {
        c2d.drawImage(f.el, dx, dy, dw, dh);
      }
      if (f.overlay) c2d.drawImage(f.overlay, dx, dy, dw, dh);
    } catch { /* a frame not yet decodable; the next tick has one */ }
    // The name, on a quiet strip at the foot of the tile.
    c2d.fillStyle = 'rgba(0, 0, 0, 0.55)';
    c2d.fillRect(dx, dy + dh - 34, Math.min(dw, 16 + f.name.length * 13), 34);
    c2d.fillStyle = '#fff';
    c2d.font = '600 20px system-ui, sans-serif';
    c2d.fillText(f.name, dx + 8, dy + dh - 11);
  });
}

async function startCapture({ sessionId, recordingId, kind, mime, micRef, meshRef, nameOf, myName }) {
  if (capture.running) return;
  const Ctor = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!Ctor || !window.MediaRecorder) throw new Error(NOT_SUPPORTED);
  capture.running = true;
  capture.job = { sessionId, recordingId, mime };
  capture.seq = 0;
  capture.sources = new Map();
  capture.ctx = new Ctor();
  if (capture.ctx.state === 'suspended' && capture.ctx.resume) { try { await capture.ctx.resume(); } catch { /* it mixes anyway */ } }
  capture.dest = capture.ctx.createMediaStreamDestination();
  mixIn(micRef?.current, meshRef);
  // People arrive and leave during a recording; look again every two seconds.
  capture.scan = setInterval(() => mixIn(micRef?.current, meshRef), 2000);

  const tracks = [...capture.dest.stream.getAudioTracks()];
  if (kind === 'video') {
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS.w;
    canvas.height = CANVAS.h;
    const c2d = canvas.getContext('2d');
    const tick = () => paintFrame(c2d, framesOnPage(nameOf, myName), CANVAS.w, CANVAS.h);
    tick();
    capture.canvas = canvas;
    capture.paint = setInterval(tick, Math.round(1000 / CANVAS.fps));
    if (typeof canvas.captureStream !== 'function') throw stopCaptureWith(new Error(NOT_SUPPORTED));
    tracks.push(...canvas.captureStream(CANVAS.fps).getVideoTracks());
  }
  capture.stream = new MediaStream(tracks);
  let rec;
  try {
    rec = new window.MediaRecorder(capture.stream, mime ? { mimeType: mime } : undefined);
  } catch (err) {
    throw stopCaptureWith(new Error(NOT_SUPPORTED));
  }
  const job = capture.job;
  rec.ondataavailable = (e) => {
    if (!e.data || e.data.size === 0) return;
    uploader.add({ ...job, seq: capture.seq, blob: e.data });
    capture.seq += 1;
  };
  rec.onstop = () => {
    // The final chunk has already been handed over (ondataavailable fires
    // before onstop), so finish follows the queue.
    uploader.end(job.sessionId, job.recordingId, Date.now() - capture.startedAt);
    tearDown();
  };
  rec.onerror = () => { try { rec.stop(); } catch { tearDown(); } };
  capture.recorder = rec;
  capture.startedAt = Date.now();
  rec.start(TIMESLICE_MS);
}

function tearDown() {
  if (capture.scan) clearInterval(capture.scan);
  if (capture.paint) clearInterval(capture.paint);
  capture.scan = null;
  capture.paint = null;
  for (const node of capture.sources.values()) { try { node.disconnect(); } catch { /* gone */ } }
  capture.sources = new Map();
  try { capture.stream?.getVideoTracks().forEach((t) => t.stop()); } catch { /* gone */ }
  if (capture.ctx && typeof capture.ctx.close === 'function') { capture.ctx.close().catch(() => {}); }
  capture.ctx = null;
  capture.dest = null;
  capture.canvas = null;
  capture.stream = null;
  capture.recorder = null;
  capture.running = false;
}

/** Started and then found wanting: undo it, and hand the reason back. */
function stopCaptureWith(err) {
  tearDown();
  return err;
}

/** Stop recording: the last chunk goes up, then finish. Safe to call twice. */
export function stopCapture() {
  const rec = capture.recorder;
  if (!rec) { if (capture.running) tearDown(); return false; }
  if (rec.state === 'inactive') { tearDown(); return false; }
  try { rec.stop(); } catch { tearDown(); }
  return true;
}

export const isCapturing = () => capture.running;

/* ------------------------------------------------------------- signals */

/** Who a signal is about, by name: the server's word, else the sender's. */
const whoOf = (s, fromName) => {
  const p = s.payload || {};
  if (p.by_name) return String(p.by_name);
  if (p.name) return String(p.name);
  if (typeof p.by === 'string' && p.by && p.by !== s.from_id && !/^[0-9a-f-]{20,}$/i.test(p.by)) return p.by;
  return fromName || 'Somebody';
};

/**
 * A 'recording' signal from somebody else on the call. SessionPage hands
 * these over from its poll handler; nothing else here reads the feed.
 *
 *   started   their recording is running - banner and a chime for everybody
 *   asked     they want to record and this page has to answer (ask mode)
 *   accepted  one person has accepted; `pending` says who is still to
 *   declined  one person said no, which cancels it for everybody
 *   stopped   it is over
 */
/** `pending` as names, whether the server sent [{ key, name }] or names. */
export const namesIn = (list) => (Array.isArray(list) ? list : [])
  .map((x) => (typeof x === 'string' ? x : x?.name || x?.display_name || null)).filter(Boolean);

export function onRecordingSignal(s, fromName) {
  const p = s?.payload || {};
  // 'asking' is the recording's own state word; as a signal it means 'asked'.
  const kind = p.state === 'asking' ? 'asked' : String(p.state || '');
  const id = p.recording_id || p.id || null;
  const who = whoOf(s, fromName);
  setRecordingState((st) => {
    const cur = st.active;
    const same = cur && (!id || !cur.id || cur.id === id);
    if (kind === 'started') {
      if (same && cur.mine) {
        return { ...st, active: { ...cur, state: 'recording', pending: [], asked: false } };
      }
      return { ...st, note: null, active: {
        id, byId: s.from_id || null, byName: who, mine: false, kind: p.kind || 'audio',
        startedAt: p.started_at ? (Date.parse(p.started_at) || Date.now()) : Date.now(),
        state: 'recording', pending: [], declined: null, asked: false
      } };
    }
    if (kind === 'asked') {
      return { ...st, note: null, active: {
        id, byId: s.from_id || null, byName: who, mine: false, kind: p.kind || 'audio',
        startedAt: Date.now(), state: 'asking',
        pending: namesIn(p.pending), declined: null, asked: true
      } };
    }
    if (!same) return st;
    if (kind === 'accepted') {
      const pending = Array.isArray(p.pending) ? namesIn(p.pending) : cur.pending.filter((n) => n !== who);
      const done = p.state === 'recording' || p.recording === true || pending.length === 0;
      return { ...st, active: { ...cur, pending, state: done && cur.state === 'asking' ? 'recording' : cur.state,
        startedAt: done && cur.state === 'asking' ? Date.now() : cur.startedAt } };
    }
    if (kind === 'declined') {
      return { ...st, active: { ...cur, state: 'declined', declined: who, reason: p.reason || null, asked: false, pending: [] } };
    }
    if (kind === 'stopped') {
      return { ...st, active: null, note: cur.mine ? null : `${cur.byName || 'The'} recording has stopped.` };
    }
    // A break in the room (RoomRecorder's pause): the banner says Paused
    // and its clock stands still until 'resumed'.
    if (kind === 'paused') return { ...st, active: { ...cur, paused: true, pausedAt: Date.now() } };
    if (kind === 'resumed') {
      const away = cur.pausedAt ? Date.now() - cur.pausedAt : 0;
      return { ...st, active: { ...cur, paused: false, pausedAt: null, startedAt: (cur.startedAt || Date.now()) + away } };
    }
    return st;
  });
  if (kind === 'started') { ping('calls'); if (document.hidden) flashTitle('This call is being recorded'); }
  if (kind === 'asked') { startRinging({ times: 4 }); buzz(); }
  if (kind === 'declined' || kind === 'stopped') stopRinging();
}

/* ------------------------------------------------------------- the control */

const Dot = ({ className = '' }) => <span className={`rb-dot ${className}`} aria-hidden="true" />;

/**
 * The footer's Record button, the choice it opens, the consent prompt in ask
 * mode, and the glue between the store, the mesh and the capture above.
 *
 *   sessionId, me, participants, phase    from the page
 *   micRef, meshRef                        the page's live streams (refs)
 *   video                                  whether this is a video call, so
 *                                          "Audio + video" is offered
 *   config                                 { enabled, consent } carried on the
 *                                          join response, if the server sent
 *                                          one - a fallback until the call's
 *                                          own GET /recordings has answered
 *
 * GET /sessions/:id/recordings is read once the call is joined. Its
 * `settings` decide whether Record is shown (recording_enabled) and whether
 * it can be pressed (storage_configured); its `active` is a recording
 * already going when this person arrived, so the banner is up for them
 * from the first moment rather than from the next signal.
 */
export default function Recorder({ sessionId, me, participants = [], phase, micRef, meshRef, video = false, config = null }) {
  const st = useRecordingState();
  const whoAmI = useMe();
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState(null);
  const [language, setLanguage] = useState('');
  const [server, setServer] = useState(null); // settings from GET /sessions/:id/recordings
  const active = st.active;
  const admin = whoAmI?.isAdmin === true || me?.is_admin === true || me?.role === 'admin';
  const settings = server || (config ? { recording_enabled: config.enabled !== false, recording_consent: config.consent,
    storage_configured: true } : null);
  // Record is drawn only when the firm's rule (Settings -> Rules) AND this
  // person's switch (078, Settings -> Features) both allow it - the server
  // folds both into recording_enabled, and the switch is read here too so
  // the button is not drawn for a moment before that answer arrives.
  const mayRecord = useFeature('recording');
  const enabled = mayRecord && (settings ? settings.recording_enabled !== false : true);
  const consent = settings?.recording_consent === 'ask' ? 'ask' : 'announce';
  const storage = settings ? settings.storage_configured !== false : true;

  useEffect(() => {
    if (!sessionId) return undefined;
    let gone = false;
    sessionRecordings(sessionId).then((r) => {
      if (gone || !r) return;
      if (r.settings) setServer(r.settings);
      const a = r.active;
      if (a && (a.state === 'recording' || a.state === 'asking')) {
        setRecordingState((s) => (s.active ? s : { ...s, active: {
          id: a.id, byId: a.started_by_id || null, byName: a.started_by_name || 'Somebody',
          mine: false, kind: a.kind || 'audio',
          startedAt: a.started_at ? (Date.parse(a.started_at) || Date.now()) : Date.now(),
          state: a.state, pending: namesIn(a.pending), declined: null,
          // Asked before I arrived: the question is still open for me.
          asked: a.state === 'asking' && namesIn(a.pending).includes(me?.display_name)
        } }));
      }
    }).catch((err) => {
      if (gone) return;
      if (notReady(err)) setRecordingState((s) => ({ ...s, hidden: true }));
    });
    return () => { gone = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const participantsRef = useRef(participants);
  participantsRef.current = participants;
  const nameOf = useCallback((id) => participantsRef.current.find((p) => p.id === id)?.display_name || null, []);
  const myName = me?.display_name || whoAmI?.name || 'You';
  const peers = participants.filter((p) => p.id !== me?.id && (p.present || p.present === undefined));

  const canStop = Boolean(active && active.state === 'recording' && (active.mine || admin));
  useEffect(() => { setRecordingState((s) => (s.canStop === canStop ? s : { ...s, canStop })); }, [canStop]);

  /* Mine, and everybody has agreed (or nobody had to): capture begins. */
  useEffect(() => {
    if (!active?.mine || active.state !== 'recording' || isCapturing()) return;
    startCapture({ sessionId, recordingId: active.id, kind: active.kind, mime: active.mime,
      micRef, meshRef, nameOf, myName })
      .catch((err) => {
        setTrouble(err?.message || NOT_SUPPORTED);
        finishRecording(sessionId, active.id, 0).catch(() => {});
        setRecordingState((s) => ({ ...s, active: null }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.mine, active?.state, active?.id]);

  /* Declined, or stopped by an administrator: whatever was running stops. */
  useEffect(() => {
    if ((!active || active.state === 'declined') && isCapturing()) stopCapture();
  }, [active]);

  /* A decline is read and then cleared, so the next attempt starts clean. */
  useEffect(() => {
    if (active?.state !== 'declined') return undefined;
    const t = setTimeout(() => setRecordingState((s) => (s.active?.state === 'declined' ? { ...s, active: null } : s)), 9000);
    return () => clearTimeout(t);
  }, [active?.state]);

  /* The call ended, or the page is going: the recording stops itself. */
  const endIt = useCallback(() => {
    const cur = getRecordingState().active;
    if (cur?.mine) {
      if (!stopCapture() && cur.state === 'asking') finishRecording(sessionId, cur.id, 0).catch(() => {});
    }
    stopRinging();
    setRecordingState((s) => ({ ...s, active: null, canStop: false }));
  }, [sessionId]);
  useEffect(() => { if (phase && phase !== 'live') endIt(); }, [phase, endIt]);
  useEffect(() => () => endIt(), [endIt]);

  /* ---- starting */

  const begin = async (kind) => {
    setChoosing(false);
    setTrouble(null);
    const mime = pickMime(kind);
    if (!mime) { setTrouble(NOT_SUPPORTED); return; }
    setBusy(true);
    try {
      const r = await startRecording(sessionId, { kind, mime, language: language || null });
      const asking = r?.state === 'asking';
      setRecordingState((s) => ({ ...s, note: null, active: {
        id: r?.id, byId: me?.id || null, byName: myName, mine: true, kind, mime,
        startedAt: Date.now(), state: asking ? 'asking' : 'recording',
        pending: asking ? (Array.isArray(r.pending) ? namesIn(r.pending) : peers.map((p) => p.display_name).filter(Boolean)) : [],
        declined: null, asked: false
      } }));
      ping('calls');
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || {};
      if (status === 409 && data.recording_id) {
        // Somebody else got there first; the signal that says so may be a
        // poll behind, so the banner is put up from this answer.
        setRecordingState((s) => ({ ...s, active: s.active || {
          id: data.recording_id, byId: null, byName: data.started_by_name || 'Somebody else', mine: false,
          kind: data.kind || 'audio', startedAt: Date.now(), state: data.state === 'asking' ? 'asking' : 'recording',
          pending: namesIn(data.pending), declined: null, asked: false
        } }));
        setTrouble('Somebody else is already recording this call.');
      } else if (status === 403 || notReady(err)) {
        setRecordingState((s) => ({ ...s, hidden: true }));
        setTrouble(notReady(err) ? 'Call recordings are not switched on for this server yet.'
          : reasonOf(err, 'Recording is not allowed for you on this call.'));
      } else if (status === 503) {
        setServer((cur) => ({ ...(cur || {}), recording_enabled: true, storage_configured: false }));
        setTrouble(reasonOf(err, 'Recording storage is not set up on this server.'));
      } else {
        setTrouble(reasonOf(err, 'Could not start recording.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    if (!active) return;
    setBusy(true);
    try {
      if (active.mine) {
        if (!stopCapture()) await finishRecording(sessionId, active.id, 0).catch(() => {});
      } else {
        // An administrator stopping somebody else's: the server tells the
        // recorder's page, which stops its MediaRecorder on 'stopped'.
        await finishRecording(sessionId, active.id, Date.now() - active.startedAt);
      }
      setRecordingState((s) => ({ ...s, active: null }));
    } catch (err) {
      setTrouble(reasonOf(err, 'Could not stop the recording.'));
    } finally {
      setBusy(false);
    }
  };

  /* ---- being asked */

  const answer = async (accept) => {
    if (!active) return;
    stopRinging();
    setBusy(true);
    try {
      const r = await answerConsent(sessionId, active.id, accept);
      setRecordingState((s) => {
        if (!s.active) return s;
        if (!accept) return { ...s, active: { ...s.active, asked: false, state: 'declined', declined: myName } };
        const pending = Array.isArray(r?.pending) ? r.pending : s.active.pending;
        const now = r?.state === 'recording' || pending.length === 0;
        return { ...s, active: { ...s.active, asked: false, pending,
          state: now ? 'recording' : 'asking', startedAt: now ? Date.now() : s.active.startedAt } };
      });
    } catch (err) {
      setTrouble(reasonOf(err, 'Could not send your answer.'));
    } finally {
      setBusy(false);
    }
  };

  // Somebody who may not record is still shown somebody else's recording,
  // and still asked in 'ask' mode: being told is not a feature to switch off.
  if (st.hidden || (!enabled && !active)) return null;
  const asking = active && active.state === 'asking';
  const uploading = st.uploads.pending;

  return (
    <>
      {/* The button. Red when recording, so the person who pressed it can
          see what state they are in without reading. */}
      {canStop ? (
        <button type="button" className="se-flat rb-btn is-on" onClick={stop} disabled={busy}
          aria-pressed="true" title="Stop recording this call">
          <Dot /> Stop recording
        </button>
      ) : active ? (
        <span className="rb-chip" role="status">
          <Dot className={asking ? 'is-amber' : ''} />
          {asking ? (active.mine ? 'Waiting…' : 'Being asked…') : `${active.byName || 'Somebody'} is recording`}
        </span>
      ) : enabled && (
        <button type="button" className="se-flat rb-btn" onClick={() => setChoosing(true)}
          disabled={busy || phase !== 'live' || !storage} data-storage={storage ? undefined : 'off'}
          title={storage ? 'Record this call' : 'Recording storage is not set up on this server'}>
          <Dot className="is-idle" /> Record
        </button>
      )}
      {enabled && !storage && !active && (
        <span className="rb-chip is-bad" role="status">Recording storage is not set up</span>
      )}

      {uploading > 0 && (
        <span className="rb-chip is-upload" role="status" aria-live="polite">
          Uploading {uploading}…
        </span>
      )}
      {st.uploads.failed && uploading === 0 && (
        <span className="rb-chip is-bad" role="status">Part of the recording did not upload</span>
      )}

      {trouble && (
        <span className="rb-trouble" role="status">
          {trouble}
          <button type="button" className="tds-link" onClick={() => setTrouble(null)}>Dismiss</button>
        </span>
      )}

      {/* Which kind. Video only when this is a video call: recording a canvas
          of empty tiles is a file nobody wants. */}
      {choosing && (
        <Dialog label="Record this call" onClose={() => setChoosing(false)} className="rb-choose">
          <p className="tds-quiet">
            {consent === 'ask'
              ? 'Everybody on the call is asked to accept first. Nothing is recorded until they all have, and one refusal cancels it.'
              : 'Everybody on the call sees a Recording banner the moment it starts, and hears a chime.'}
            {' '}The recording is transcribed afterwards and kept with the call.
          </p>
          {/* What the transcriber listens for. Left alone it is the firm's
              default (Settings -> Rules) - Malti + English unless changed -
              which suits a Maltese call that drifts between the two; naming
              one language helps a call that is all in one. */}
          <label className="tds-field rb-lang">
            <span>Transcribe in</span>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} aria-label="Language of the call" data-rec-language="">
              <option value="">Firm default ({defaultLanguageWords(settings?.recording_language || 'mixed')})</option>
              {RECORDING_LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </label>
          {(language === 'mixed' || (!language && (settings?.recording_language || 'mixed') === 'mixed')) && (
            <p className="tds-quiet rb-lang-hint" data-rec-language-hint="">{MIXED_HINT}</p>
          )}
          <div className="rb-kinds">
            <button type="button" className="rb-kind" data-kind="audio" onClick={() => begin('audio')}>
              <span className="rb-kind-head">Audio only</span>
              <span className="rb-kind-note">Everybody&rsquo;s voices, mixed. Small, and enough for a transcript.</span>
            </button>
            {video && (
              <button type="button" className="rb-kind" data-kind="video" onClick={() => begin('video')}>
                <span className="rb-kind-head">Audio + video</span>
                <span className="rb-kind-note">The voices and whatever is on screen &mdash; cameras and shared screens &mdash; at 720p.</span>
              </button>
            )}
          </div>
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" onClick={() => setChoosing(false)}>Cancel</button>
          </div>
        </Dialog>
      )}

      {/* Ask mode, the other end: a question that stops everything else until
          it is answered, like being asked to share a screen. */}
      {active?.asked && asking && (
        <Dialog label={`${active.byName || 'Somebody'} wants to record this call`} onClose={() => answer(false)}
          className="rb-consent">
          <p className="tds-quiet">
            If you accept, the call is recorded from here on &mdash; your voice included &mdash; and a
            transcript is made afterwards. Everybody on the call has to accept; declining cancels it.
          </p>
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn rb-decline" disabled={busy} onClick={() => answer(false)}>
              Decline
            </button>
            <button type="button" className="btn btn-primary tds-btn rb-accept" disabled={busy} onClick={() => answer(true)}>
              Accept
            </button>
          </div>
        </Dialog>
      )}
      {/* The recorder's side of the same wait. */}
      {active?.mine && asking && (
        <span className="rb-chip is-wait" role="status" aria-live="polite">
          Waiting for {active.pending.length ? active.pending.join(', ') : 'them'}…
        </span>
      )}
    </>
  );
}

